import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Loader } from "../../../shared/Loader";

interface TrendData {
  timestamp: number;
  usd: number;
  ngn: number;
}

interface MarketTrendChartProps {
  data: TrendData[] | undefined;
  isLoading: boolean;
  currency: "usd" | "ngn";
}

export const MarketTrendChart: React.FC<MarketTrendChartProps> = ({
  data,
  isLoading,
  currency = "usd",
}) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item) => ({
      time: new Date(item.timestamp).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      value: currency === "usd" ? item.usd : item.ngn,
      timestamp: item.timestamp,
    }));
  }, [data, currency]);

  if (isLoading) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-64 flex items-center justify-center text-gray-400">
        No trend data available
      </div>
    );
  }

  // Determine if the trend is positive or negative
  const firstValue = chartData[0]?.value || 0;
  const lastValue = chartData[chartData.length - 1]?.value || 0;
  const isPositive = lastValue >= firstValue;
  const lineColor = isPositive ? "#22c55e" : "#ef4444"; // green-500 or red-500

  return (
    <div className="w-full h-72">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <XAxis
            dataKey="time"
            stroke="#4b5563"
            strokeWidth={1}
            tick={{ fill: "#6b7280", fontSize: 11 }}
            axisLine={{ stroke: "#374151", strokeWidth: 1 }}
            tickLine={{ stroke: "#374151", strokeWidth: 1 }}
            interval={Math.floor(chartData.length / 3)}
            minTickGap={50}
          />
          <YAxis
            stroke="#4b5563"
            strokeWidth={1}
            tick={{ fill: "#6b7280", fontSize: 11 }}
            axisLine={{ stroke: "#374151", strokeWidth: 1 }}
            tickLine={{ stroke: "#374151", strokeWidth: 1 }}
            domain={["auto", "auto"]}
            tickFormatter={(value) => {
              if (currency === "usd") {
                return value.toFixed(2);
              } else {
                if (value >= 1000) {
                  return `${(value / 1000).toFixed(1)}k`;
                }
                return value.toFixed(0);
              }
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1f2937",
              border: "1px solid #374151",
              borderRadius: "8px",
              color: "#f3f4f6",
            }}
            labelStyle={{ color: "#9ca3af" }}
            formatter={(value: any) => [
              `${currency.toUpperCase()}: ${(value ?? "0")?.toFixed(2)}`,
              "",
            ]}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={lineColor}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: lineColor }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

